import React, { useEffect, useRef, useState, useCallback } from "react";
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from "use-places-autocomplete";
import { useNavigate,useLocation } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import {
  updateFilters,
  setFilters,
  deleteFilters,
} from "store/filters/operations";
import { useTranslation } from "react-i18next";
import { selectFilters } from "store/filters/selectors";
import { getQueryParamsFromFilters } from "helpers/searchParams";
import searchIcon from "../../../../assets/images/search.svg";
import { poweredByGoogle } from "assets/icons";
import styles from "./styles.module.scss";
import { getLocationNameByLatLng } from "helpers/getLocationNameByLatLng";
import useDebounce from "hooks/useDebounce";

// Function to fetch geocode data
const fetchGeocodeData = async (address) => {
  const results = await getGeocode({ address });
  const { lat, lng } = await getLatLng(results[0]);

  const cityComponent = results[0].address_components.find(
    (component) =>
      component.types.includes("locality") ||
      component.types.includes("administrative_area_level_2")
  );
  const city = cityComponent ? cityComponent.long_name : null;

  return { lat, lng, city };
};

const SearchInput = ({ withStyledInput = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [t] = useTranslation("global");
  const filters = useSelector(selectFilters);
  const [selectedAddress, setSelectedAddress] = useState();
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const dropdownRef = useRef(null);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);

  useEffect(() => {
    const checkGoogleMapsLoaded = () => {
      if (window.google) {
        setGoogleMapsLoaded(true);
      } else {
        setTimeout(checkGoogleMapsLoaded, 100);
      }
    };
    checkGoogleMapsLoaded();
  }, []);

  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    debounce: 30, // Adding debounce directly to the PlacesAutocomplete hook
  });

  // Debounced input value for optimized performance
  const debouncedValue = useDebounce(value, 30);

  useEffect(() => {
    const fetchLocationData = async () => {
      // Set initial value based on filters (text, city, or lat/lng data)
      if (filters.text) {
        setValue(filters.text);
        setSelectedAddress(filters.text);
      } else if (filters.city) {
        setValue(filters.city);
        setSelectedAddress(filters.city);
      } else if (filters.lat && filters.lng) {
        const latLngString = await getLocationNameByLatLng(
          filters.lat,
          filters.lng
        );
        setValue(latLngString);
        setSelectedAddress(latLngString);
      } else if (
        filters.lat_p1 &&
        filters.lng_p1 &&
        filters.lat_p2 &&
        filters.lng_p2
      ) {
        const latLngRangeString = await getLocationNameByLatLng(
          (filters.lat_p1 + filters.lat_p2) / 2,
          (filters.lng_p1 + filters.lng_p2) / 2
        );
        setValue(latLngRangeString);
        setSelectedAddress(latLngRangeString);
      } else {
        setValue("");
        setSelectedAddress("");
      }
    };

    fetchLocationData();
  }, [
    googleMapsLoaded,
    filters.lat,
    filters.lng,
    filters.lat_p1,
    filters.lat_p2,
    filters.lng_p1,
    filters.lng_p2,
    filters.city,
    filters.text,
    setValue,
    // filters
  ]);

  const handleSelect = useCallback(
    async (address) => {
      setValue(address, false);
      clearSuggestions();
      setIsDropdownVisible(false);
      setSelectedAddress(address);

      try {
        const { lat, lng, city } = await fetchGeocodeData(address);

        dispatch(setFilters({ text: address, lat, lng, city }));

        // Navigate after dispatching to ensure state is updated first
        const queryParams = getQueryParamsFromFilters({
          text: address,
          lat,
          lng,
          city,
        });
        if (location.pathname !== '/search') {
          navigate(`/search?${queryParams}`);
        }      } catch (error) {
        console.error("Error fetching place details:", error);
      }
    },
    [dispatch, clearSuggestions, setValue, navigate]
  );

  const handleSearchIconClick = useCallback(async () => {
    if (!debouncedValue) {
      dispatch(
        deleteFilters(
          "text",
          "lat",
          "lng",
          "city",
          "vicinity",
          "lat_p1",
          "lat_p2",
          "lng_p1",
          "lng_p2"
        )
      );
      setSelectedAddress(null);
      return;
    }

    let addressToSearch = debouncedValue;

    // Use the first suggestion if available and user input is not already matching
    if (
      status === "OK" &&
      data.length > 0 &&
      debouncedValue !== data[0].description
    ) {
      addressToSearch = data[0].description;
      setValue(addressToSearch, false);
      setSelectedAddress(addressToSearch);
      setIsDropdownVisible(false);
    } else if (!selectedAddress) {
      setSelectedAddress(debouncedValue);
    }

    try {
      const { lat, lng, city } = await fetchGeocodeData(addressToSearch);

      dispatch(setFilters({ text: addressToSearch, lat, lng, city }));
      clearSuggestions();

      // Navigate after dispatching to ensure state is updated first
      const queryParams = getQueryParamsFromFilters({
        text: addressToSearch,
        lat,
        lng,
        city,
      });
      navigate(`/search?${queryParams}`);
    } catch (error) {
      console.error("Error fetching place details:", error);
    }
  }, [
    debouncedValue,
    data,
    selectedAddress,
    setValue,
    status,
    navigate,
    dispatch,
    clearSuggestions,
  ]);

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsDropdownVisible(false);
    }
  };

  useEffect(() => {
    if (isDropdownVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownVisible]);

  return (
    <form className={styles.searchForm} ref={dropdownRef}>
      <input
        type="text"
        id="searchText"
        autoComplete="off"
        className={
          withStyledInput ? styles.searchInputStyled : styles.searchInputDefault
        }
        placeholder={t("searchBar.searchPlaceholder")}
        value={debouncedValue}
        onChange={(e) => {
          setValue(e.target.value);
          setIsDropdownVisible(e.target.value !== "");
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleSearchIconClick();
          }
        }}
        disabled={!ready}
      />
      <img
        src={searchIcon}
        alt="search icon"
        className={styles.searchIcon}
        onClick={handleSearchIconClick}
      />

      {isDropdownVisible && (
        <div className={styles.dropdownBody}>
          {status === "OK" &&
            data.slice(0, 5).map(({ place_id, description }) => (
              <div
                key={place_id}
                className={styles.dropdownItem}
                onClick={() => handleSelect(description)}
              >
                {description}
              </div>
            ))}
          <img
            className={styles.poweredByGoogle}
            src={poweredByGoogle}
            alt="powered by google"
          />
        </div>
      )}
    </form>
  );
};

export default SearchInput;
